import {
  AdditionalCostFeeType,
  ProposalAdditionalCost,
  ProposalProduct,
  ProposalTransferTruck,
} from "generated/graphql";
import { mapObjIndexed, mergeRight, omit } from "ramda";
import removeKeysInArray from "utils/object/removeKeysInArray";
import setNullValuesTo from "utils/object/setNullValuesTo";
import getAttributesToSkip from "utils/products/getAttributesToSkip";
import * as z from "zod";

export const AddTruckToProposalProductInputResolver = z.object({
  proposalProductId: z.string().min(1, "Proposal product Id is required"),
  transferTruckId: z.string().min(1, "Proposal product Id is required"),
  truckingProductionRate: z.coerce.number().min(0).optional(),
  rate: z.coerce.number().min(0).optional(),
});

export const UpdateProposalProductInputResolver = z.object({
  /* end local values */
  id: z.string().min(1, "Product Id is required"),
  jobSize: z.number().positive().or(z.literal("")),
  mapData: z.string().optional(),
  notes: z.string().optional(),
  overallTotalOverride: z.literal("").or(z.number().positive().optional()),
  jobDurationDaysOverride: z.literal("").or(z.number().positive().optional()),
  jobDurationOverride: z.literal("").or(z.number().positive().optional()),
  roundTripTravelTimeHours: z.literal("").or(z.number().positive().optional()),
  profitPercentageOverride: z
    .literal("")
    .or(
      z
        .number()
        .positive("Profit Margin % must be greater than 0")
        .max(99, "Profit Margin % must be less than 100")
        .optional()
    ),
  material: z.object({
    unitPrice: z.literal("").or(z.number().optional()),
    markup: z.literal("").or(z.number().positive()),
    overheadPercentage: z.literal("").or(z.number().optional()),
    indirectPercentage: z.literal("").or(z.number().optional()),
    statementOfWork: z.string().min(1),
    costMaterial: z.literal("").or(z.number().optional()),
    dailyUnitsComplete: z.literal("").or(z.number().positive()),
    materialNeeded: z.literal("").or(z.number().optional()),
    attributes: z
      .object({
        applicationRate: z.number().optional(),
        asphaltDepth: z.number().optional(),
        crackDepth: z.number().optional(),
        crackWidth: z.number().optional(),
        curbHeight: z.number().optional(),
        curbWidth: z.number().optional(),
        gutterHeight: z.number().optional(),
        gutterWidth: z.number().optional(),
        lineWidth: z.number().optional(),
        overbandThickness: z.number().optional(),
        overbandWidth: z.number().optional(),
        poundsPerBlock: z.number().optional(),
        poundsPerGallon: z.number().optional(),
        transferTruckLoadTime: z.number().optional(),
        transferTruckUnloadTime: z.number().optional(),
        height: z.number().optional(),
        width: z.number().optional(),
        diameter: z.number().optional(),
        depth: z.number().optional(),
      })
      .optional(),
    tonConversionFactor: z.literal("").or(z.number()).optional(),
  }),
  equipmentPieces: z.array(
    z.object({
      id: z.string().min(1, "Equipment Piece Id is Required"),
      quantity: z.number().min(0, "Quantity must be 0 or greater").optional(),
      rate: z.number().optional(),
      timeOnJob: z.coerce.number().min(0).optional(),
      timeTravel: z.coerce.number().min(0).optional(),
    })
  ),
  additionalCosts: z.array(
    z.object({
      id: z.string().min(1, "Additional Cost Id is Required"),
      cost: z.number(),
      feeAmount: z.number(),
      quantity: z.number().min(1),
      showOnProposal: z.boolean(),
    })
  ),
  crews: z.array(
    z.object({
      id: z.string().min(1, "Crew Id is Required"),
      quantity: z.number().optional(),
      rate: z.number().optional(),
      timeOnjob: z.coerce.number().min(0).optional(),
      timeTravel: z.coerce.number().min(0).optional(),
    })
  ),
  plant: z
    .object({
      rateOnWeight: z.boolean(),
    })
    .optional(),
  plantProducts: z
    .array(
      z.object({
        id: z.string().min(1, "Plant Product Id is Required"),
        materialNeededOverride: z.number().optional(),
        rateOverride: z.number().optional(),
        applicationRateOverride: z.number().optional(),
        areaCoverage: z.number().optional(),
        rateOnWeight: z.boolean().optional(),
        transferTruckRoundTripTimeHrsOverride: z.number().optional(),
        transferTruckLoadTimeBuffer: z.number().optional(),
        transferTruckUnloadTimeBuffer: z.number().optional(),
      })
    )
    .optional(),
  transferTrucks: z.array(
    z.object({
      plantTransferTruckId: z.string().min(1, "Plant Transfer Truck Id is Required"),
      quantity: z.number(),
      rate: z.number().optional(),
    })
  ),
  subcontractorCost: z.literal("").or(z.number().positive().optional()),
  subcontractorName: z.string().optional(),
  subcontractorProposalId: z.string().optional(),
  materialNoPlants: z
    .array(
      z.object({
        id: z.string().min(1, "Material Id is Required"),
        name: z.string().min(1, "Material name is required"),
        percentage: z.number().min(0).max(100),
        rate: z.number().min(0),
        quantityOverride: z.number().min(1).nullable().optional(),
      })
    )
    .optional(),
});

export type UpdateProposalProductInputSchema = z.infer<typeof UpdateProposalProductInputResolver>;
export type AddTruckToProposalProductInputSchema = z.infer<
  typeof AddTruckToProposalProductInputResolver
>;

export type AdditionalCost = UpdateProposalProductInputSchema["additionalCosts"];

export const convertHrsToMins = (field) =>
  typeof field === "number" ? +(field * 60).toFixed(2) : null;

const normalizePlantProductRateOverride = (
  rateOverride: number | null | undefined,
  rate: number | null | undefined
) => {
  if (typeof rateOverride !== "number" || Number.isNaN(rateOverride)) {
    return null;
  }

  // Some proposal responses already return the override in dollars while the base rate remains cents.
  // If the stored override is ~100x smaller than the base rate, treat it as dollars and keep it as-is.
  if (typeof rate === "number" && !Number.isNaN(rate) && rate > 0) {
    const expectedDollarRate = rate / 100;
    if (Math.abs(rateOverride - expectedDollarRate) < 0.001) {
      return rateOverride;
    }
  }

  return rateOverride / 100;
};

export const getDefaultValues = ({
  product,
  transferTrucks,
  averageWorkingHoursPerDay,
}: {
  product: ProposalProduct;
  transferTrucks: ProposalProduct["transferTrucks"];
  averageWorkingHoursPerDay: number;
}): UpdateProposalProductInputSchema => {
  const attributesToSkip = getAttributesToSkip(product.material.organizationProductTypeName);

  const allTransferTrucks = (product.proposalPlantProducts || []).flatMap(
    (ppp) => ppp.transferTrucks || []
  );

  const productTransferTrucksMap = allTransferTrucks.reduce(
    (acc: Record<ProposalTransferTruck["transferTruckId"], ProposalTransferTruck>, val) => {
      return {
        ...acc,
        [val.plantTransferTruckId]: val,
      };
    },
    {}
  );

  // const mergedAttributes = mergeRight(
  //   product.material.attributes,
  //   product.material.attributesOverride
  // );

  const attributesWithoutTypename = omit(["__typename"])(product.material.attributes);
  const attributes = removeKeysInArray(attributesToSkip)(attributesWithoutTypename);
  const attributesOverrides = Object.keys(attributes).reduce((acc, val) => {
    return {
      ...acc,
      [val]: product.material.attributesOverride?.[val] ?? null,
    };
  }, {});

  const handleTimeDisplay = (field) =>
    typeof field === "number" ? +(field / 3600).toFixed(2) : null;

  return {
    id: product.id,
    jobSize: product.jobSize ?? null,
    mapData: product.mapData,
    notes: product.notes ?? null,
    overallTotalOverride: product.overallTotalOverride ? +product.overallTotalOverride : null,
    profitPercentageOverride: product.profitPercentageOverride ?? null,
    jobDurationDaysOverride: product.jobDurationDaysOverride ?? null,
    jobDurationOverride: product.jobDurationOverride ?? null,
    roundTripTravelTimeHours: product.roundTripTravelTimeHours ?? null,
    material: {
      markup: product.material.markupPercentageOverride ?? product.material.markupPercentage ?? 0,
      costMaterial: product.material.costMaterialOverride
        ? +product.material.costMaterialOverride
        : null,
      dailyUnitsComplete: product.material.dailyUnitsCompleteOverride
        ? +product.material.dailyUnitsCompleteOverride
        : null,
      materialNeeded: product?.material?.materialNeededOverride ?? null,
      attributes: attributesOverrides,
      statementOfWork:
        product.material.statementOfWorkOverride ?? product.material.statementOfWork ?? null,
      tonConversionFactor: product.material.tonConversionFactorOverride ?? null,
      overheadPercentage:
        product?.material?.overheadPercentageOverride ?? product?.material?.overheadPercentage ?? 0,
      indirectPercentage:
        product?.material?.indirectPercentageOverride ?? product?.material?.indirectPercentage ?? 0,
      unitPrice: product?.material?.unitPriceOverride ?? product?.material?.unitPrice ?? null,
    },
    equipmentPieces:
      product.equipmentPieces?.map((equipmentPiece) => ({
        id: equipmentPiece.id,
        rate: equipmentPiece.rateOverride ?? null,
        quantity: equipmentPiece.quantityOverride ?? null,
        timeTravel: handleTimeDisplay(equipmentPiece.timeTravelOverride),
        timeOnJob: handleTimeDisplay(equipmentPiece.timeOnjobOverride),
      })) ?? [],
    additionalCosts:
      product.additionalCosts?.map((additionalCost) => ({
        id: additionalCost.id,
        cost: additionalCost.costOverride ?? null,
        feeAmount: additionalCost.feeAmountOverride ?? null,
        quantity: additionalCost.quantityOverride ?? null,
        showOnProposal: additionalCost.showOnProposal ?? false,
      })) ?? [],
    crews:
      product.crews?.map((crew) => ({
        id: crew.id,
        quantity: crew.quantityOverride ?? null,
        rate: crew.rateOverride ?? null,
        timeOnjob: handleTimeDisplay(crew.timeOnjobOverride),
        timeTravel: handleTimeDisplay(crew.timeTravelOverride),
      })) ?? [],
    plant: {
      rateOnWeight: product.plantProduct?.plant?.rateOnWeight ?? false,
    },
    plantProducts: (product.proposalPlantProducts || []).map((plantProduct) => ({
      id: plantProduct.id,
      materialNeededOverride: plantProduct.materialNeededOverride ?? null,
      rateOverride: normalizePlantProductRateOverride(
        plantProduct.rateOverride,
        plantProduct.rate
      ),
      applicationRateOverride: plantProduct.applicationRateOverride ?? null,
      areaCoverage: plantProduct.areaCoverage ?? null,
      rateOnWeight: plantProduct.rateOnWeight ?? null,
      transferTruckRoundTripTimeHrsOverride:
        plantProduct.transferTruckRoundTripTimeHrsOverride ?? null,
      transferTruckLoadTimeBuffer: convertHrsToMins(plantProduct.transferTruckLoadTimeBuffer),
      transferTruckUnloadTimeBuffer: convertHrsToMins(plantProduct.transferTruckUnloadTimeBuffer),
    })),
    materialNoPlants: (product.proposalMaterialNoPlants || []).map((materialNoPlant) => ({
      id: materialNoPlant.id,
      name: materialNoPlant.name,
      percentage: materialNoPlant.percentage,
      rate: materialNoPlant.rate,
      quantityOverride: materialNoPlant.quantityOverride,
    })),
    subcontractorCost: product?.subcontractorCost ? +product?.subcontractorCost : 0,
    subcontractorName: product?.subcontractorName ?? "",
    subcontractorProposalId: product?.subcontractorProposalId ?? "",

    transferTrucks: transferTrucks.map((plantTransferTruck) => ({
      plantTransferTruckId: plantTransferTruck.id,
      quantity: productTransferTrucksMap[plantTransferTruck.id]?.quantity ?? 0,
      // rate depends on wether it's by weight or hourly
      rate: product.plantProduct?.rateOnWeight
        ? // Rate Weight
          productTransferTrucksMap[plantTransferTruck.id]?.rateTonOverride ?? null
        : // Rate Hourly
          productTransferTrucksMap[plantTransferTruck.id]?.rateHourlyOverride ?? null,
    })),
  };
};
